// ----- Phase phase-ui-consistency-sweep (§3C) — shared button styling -----
//
// The style variants live in their own module (separate from Button.tsx)
// so the component file only exports a component — keeping React Fast
// Refresh happy — while non-`<button>` elements (e.g. a react-router
// `<Link>` styled as a button) can import `buttonStyle()` directly.

import type { CSSProperties } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'ghost' | 'ghost-on-dark';
export type ButtonSize = 'sm' | 'md';

const sizeStyle: Record<ButtonSize, CSSProperties> = {
  sm: { padding: '4px 10px', fontSize: 12 },
  md: { padding: '7px 16px', fontSize: 13 },
};

function variantStyle(variant: ButtonVariant): CSSProperties {
  switch (variant) {
    case 'primary':
      return { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' };
    case 'secondary':
      return { background: '#fff', color: 'var(--accent)', border: '1px solid var(--accent)' };
    case 'destructive':
      return { background: 'var(--red)', color: '#fff', border: '1px solid var(--red)' };
    // phase-ui-hero-right-metadata-contrast — 'ghost' inherits the DARK text
    // color, which is invisible on the navy hero band (Sharon's ghosted
    // Settings button). Controls placed on navy use this light twin instead.
    case 'ghost-on-dark':
      return {
        background: 'transparent',
        color: 'rgba(255,255,255,0.92)',
        border: '1px solid rgba(255,255,255,0.45)',
      };
    case 'ghost':
    default:
      return { background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)' };
  }
}

/**
 * The shared button style. Exported so non-`<button>` elements can match
 * the `<Button>` component exactly.
 */
export function buttonStyle(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  disabled = false,
): CSSProperties {
  return {
    ...sizeStyle[size],
    ...variantStyle(variant),
    borderRadius: 6,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  };
}
